import {type LayoutType} from '@wandb/weave/common/types/media';
import React, {type CSSProperties, type FC} from 'react';

import {type BoxControlState, type MaskControlState} from './controlsImage';
import {
  CardImage,
  type CardImageProps,
  defaultHideImageState,
} from './ImageWithOverlays';

export type ImageOverlayLayoutProps = Omit<
  CardImageProps,
  'hideImage' | 'style'
> & {
  tileLayout?: LayoutType;
  hideImage?: boolean;
  // Fixed-size containers such as table cells clip tiles instead of scrolling.
  clip?: boolean;
};

const ROW_STYLE = {
  display: 'flex',
  height: '100%',
  width: '100%',
  overflow: 'auto',
} as const;
const CLIP_STYLE: CSSProperties = {
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
};
const CLIPPED_CARD_STYLE: CSSProperties = {overflow: 'hidden'};

// Renders an image with its box and mask overlays in the tile layout chosen in
// `ControlsImageOverlays`. Callers resolve URLs and controls.
export const ImageOverlayLayout: FC<ImageOverlayLayoutProps> = ({
  tileLayout,
  hideImage,
  clip,
  image,
  masks,
  boundingBoxes,
  classSets,
  boxSliders,
  boxControls,
  maskControls,
}) => {
  const defaultHideImage = defaultHideImageState(tileLayout);
  const resolvedHideImage = hideImage ?? defaultHideImage;
  const clipStyle = clip ? CLIP_STYLE : undefined;
  const cardStyle = clip ? CLIPPED_CARD_STYLE : undefined;
  const sharedProps = {
    image,
    boundingBoxes,
    classSets,
    boxSliders,
    style: cardStyle,
  };

  if (tileLayout === 'MASKS_NEXT_TO_IMAGE') {
    return (
      <div style={{...ROW_STYLE, ...clipStyle}}>
        <div style={{flex: 1, ...clipStyle}}>
          <CardImage image={image} hideImage={false} style={cardStyle} />
        </div>
        <div style={{flex: 1, ...clipStyle}}>
          <CardImage
            {...sharedProps}
            masks={masks}
            maskControls={maskControls}
            boxControls={boxControls}
            hideImage={resolvedHideImage}
          />
        </div>
      </div>
    );
  }

  if (tileLayout === 'ALL_SPLIT') {
    const controls: Array<[MaskControlState, BoxControlState | undefined]> = (
      maskControls ?? []
    ).map(mc => [mc, boxControls?.find(bc => bc.name === mc.name)]);
    const otherBoxes = (boxControls ?? []).filter(bc =>
      (maskControls ?? []).every(mc => bc.name !== mc.name)
    );
    return (
      <div style={{...ROW_STYLE, ...clipStyle}}>
        <div style={{minHeight: '200px', flex: 1, ...clipStyle}}>
          <CardImage
            {...sharedProps}
            boxControls={otherBoxes}
            hideImage={resolvedHideImage}
          />
        </div>
        {controls.map(([mc, bc], i) => (
          // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
          <div key={i} style={{minHeight: '200px', flex: 1, ...clipStyle}}>
            <CardImage
              {...sharedProps}
              masks={masks?.slice(i, i + 1)}
              maskControls={mc ? [mc] : undefined}
              boxControls={bc ? [bc] : undefined}
              hideImage={mc.hideImage ?? defaultHideImage}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <CardImage
      {...sharedProps}
      masks={masks}
      maskControls={maskControls}
      boxControls={boxControls}
      hideImage={resolvedHideImage}
    />
  );
};
